/**
 * BootSequence.jsx
 * Full-screen boot loader: shimmering progress bar,
 * "Calibrating Systems" status label, blurred dark backdrop.
 * Calls onComplete() when the fade-out finishes.
 *
 * Icy-grey theme. Per design requirement: NO serif typeface anywhere in
 * this animation — DM Sans / DM Mono only.
 */
import { useEffect, useRef, useState } from "react";

const BOOT_DURATION = 2200; // ms, matches reference timing
const FADE_DURATION = 900;  // ms

export default function BootSequence({ onComplete }) {
  const [pct, setPct] = useState(0);
  const [fading, setFading] = useState(false);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    startRef.current = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - startRef.current) / BOOT_DURATION);
      setPct(Math.round(t * 100));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setFading(true);
        setTimeout(() => {
          if (onComplete) onComplete();
        }, FADE_DURATION);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [onComplete]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 30,
        background: "rgba(38,43,54,0.72)",
        backdropFilter: "blur(46px)",
        WebkitBackdropFilter: "blur(46px)",
        opacity: fading ? 0 : 1,
        pointerEvents: fading ? "none" : "auto",
        transition: `opacity ${FADE_DURATION}ms cubic-bezier(0.16,1,0.3,1)`,
      }}
    >
      <div
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "10px",
          letterSpacing: "0.42em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.55)",
        }}
      >
        Digital Billions Group
      </div>

      {/* Progress bar — now the sole visual centerpiece of the loader */}
      <div
        style={{
          width: "min(420px, 64vw)",
          height: 3,
          background: "rgba(255,255,255,0.14)",
          position: "relative",
          overflow: "hidden",
          borderRadius: 2,
        }}
      >
        {/* Fill */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            width: `${pct}%`,
            background: "linear-gradient(90deg, #4A7191, #7FA1C2)",
            boxShadow: "0 0 16px rgba(127,161,194,0.7)",
            transition: "width 0.1s linear",
            overflow: "hidden",
            borderRadius: 2,
          }}
        >
          {/* Shimmer sweep — travels west to east, clipped to the fill itself */}
          <div className="boot-bar-shimmer" />
        </div>
      </div>

      <div
        className="boot-calibrating"
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "9px",
          letterSpacing: "0.34em",
          textTransform: "uppercase",
          color: "rgba(201,214,227,0.7)",
        }}
      >
        Calibrating Systems
      </div>
    </div>
  );
}
